import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import MarkEmailReadOutlined from '@mui/icons-material/MarkEmailReadOutlined';
import {
  getCurrentUser,
  isAuthenticated,
  logout,
  previewJoinLink,
  registerWithJoinLink,
  resendVerificationEmail,
} from '../services/auth';
import {
  PublicCardHeader,
  PublicPageFrame,
  publicBodyFontFamily,
  publicPalette,
  publicPrimaryButtonSx,
  publicSecondaryButtonSx,
  publicTextFieldSx,
} from '../components/common/PublicPageFrame';

const checkboxSx = {
  color: publicPalette.heroBlueMid,
  '&.Mui-checked': {
    color: publicPalette.heroBlueMid,
  },
};

const bodyTextSx = {
  color: publicPalette.inkSoft,
  fontFamily: publicBodyFontFamily,
  lineHeight: 1.65,
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const describeDomains = (domains = []) => domains.map((domain) => `@${domain}`).join(' or ');

function JoinUniversityPage() {
  const { token = '' } = useParams();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blockingError, setBlockingError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeTerms: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [resendState, setResendState] = useState({ status: 'idle', message: '' });
  const [signedInUser, setSignedInUser] = useState(() => (isAuthenticated() ? getCurrentUser() : null));

  const universityName = details?.university?.name || 'your university';
  const allowedDomains = useMemo(() => details?.allowedDomains || [], [details]);

  useEffect(() => {
    let isActive = true;

    const loadDetails = async () => {
      setLoading(true);
      setBlockingError('');
      try {
        const data = await previewJoinLink(token);
        if (isActive) {
          setDetails(data);
        }
      } catch (error) {
        if (!isActive) {
          return;
        }
        const data = error.response?.data;
        if (data?.university) {
          setDetails(data);
        }
        setBlockingError(data?.message || 'We could not check this sign-up link. Please try again in a moment.');
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    loadDetails();

    return () => {
      isActive = false;
    };
  }, [token]);

  const validateForm = () => {
    const errors = {};
    const email = formData.email.trim().toLowerCase();

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!EMAIL_PATTERN.test(email)) {
      errors.email = 'Enter a valid email address';
    } else if (allowedDomains.length && !allowedDomains.includes(email.split('@')[1])) {
      errors.email = `Use your ${describeDomains(allowedDomains)} email address`;
    }
    if (!formData.password || formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }
    if (formData.confirmPassword !== formData.password) {
      errors.confirmPassword = 'Passwords do not match';
    }
    if (!formData.agreeTerms) {
      errors.agreeTerms = 'You must accept the terms to continue';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const email = formData.email.trim().toLowerCase();
      const response = await registerWithJoinLink({
        joinToken: token,
        email,
        password: formData.password,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
      });
      setRegisteredEmail(email);
      setSuccessMessage(response.message || '');
    } catch (error) {
      setSubmitError(error.response?.data?.message || 'We could not create your account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setResendState({ status: 'sending', message: '' });
    try {
      const response = await resendVerificationEmail(registeredEmail);
      setResendState({ status: 'sent', message: response.message || 'A fresh verification link is on its way.' });
    } catch (error) {
      setResendState({
        status: 'error',
        message: error.response?.data?.message || 'Unable to resend the email right now. Please try again shortly.',
      });
    }
  };

  const handleSignOut = () => {
    logout();
    setSignedInUser(null);
  };

  const renderDetails = () => (
    <Box
      sx={{
        borderRadius: '22px',
        p: 2.5,
        bgcolor: alpha(publicPalette.heroBlue, 0.06),
        border: `1px solid ${alpha(publicPalette.heroBlueMid, 0.12)}`,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        {details?.university?.logoUrl && (
          <Box
            component="img"
            src={details.university.logoUrl}
            alt=""
            onError={(event) => { event.currentTarget.style.display = 'none'; }}
            sx={{ width: 64, height: 64, objectFit: 'contain', borderRadius: '14px', bgcolor: '#fff', p: 0.75, flexShrink: 0 }}
          />
        )}
        <Stack spacing={0.4} sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 800, color: publicPalette.ink, fontFamily: publicBodyFontFamily }}>
            {universityName}
          </Typography>
          {details?.label && (
            <Typography sx={bodyTextSx}>{details.label}</Typography>
          )}
          {details?.role === 'instructor' && (
            <Typography sx={bodyTextSx}>You will join as an instructor.</Typography>
          )}
          {details?.cohorts?.length > 0 && (
            <Typography sx={bodyTextSx}>{`Cohort: ${details.cohorts.join(', ')}`}</Typography>
          )}
        </Stack>
      </Stack>
    </Box>
  );

  const renderSuccess = () => (
    <Stack spacing={3} alignItems="flex-start">
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(publicPalette.heroBlue, 0.12),
          color: publicPalette.heroBlueMid,
        }}
      >
        <MarkEmailReadOutlined sx={{ fontSize: 34 }} />
      </Box>
      <Stack spacing={1}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: publicPalette.ink, fontFamily: publicBodyFontFamily }}>
          Check your inbox
        </Typography>
        <Typography sx={bodyTextSx}>
          {successMessage || `We've sent a verification link to ${registeredEmail}.`}
        </Typography>
        <Typography sx={bodyTextSx}>
          {`Once your email is verified you can sign in to Virtual Medic and start practising with ${universityName}.`}
        </Typography>
      </Stack>
      {resendState.message && (
        <Alert severity={resendState.status === 'error' ? 'error' : 'success'} sx={{ width: '100%' }}>
          {resendState.message}
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        <Button component={Link} to="/login" variant="contained" sx={publicPrimaryButtonSx}>
          Go to sign in
        </Button>
        <Button component={Link} to="/downloads" variant="outlined" sx={publicSecondaryButtonSx}>
          Download Virtual Medic
        </Button>
      </Box>
      <Button
        onClick={handleResend}
        disabled={resendState.status === 'sending' || resendState.status === 'sent'}
        sx={{ textTransform: 'none', fontWeight: 700, px: 0, color: publicPalette.heroBlueMid }}
      >
        {resendState.status === 'sending' ? 'Sending…' : "Didn't get the email? Send it again"}
      </Button>
    </Stack>
  );

  const renderForm = () => (
    <Stack spacing={3}>
      {renderDetails()}

      {signedInUser && (
        <Alert
          severity="info"
          sx={{ overflowWrap: 'anywhere' }}
          action={<Button color="inherit" size="small" onClick={handleSignOut}>Sign out</Button>}
        >
          {`You're signed in as ${signedInUser.email || 'another user'}. Sign out to create a new account with this code.`}
        </Alert>
      )}

      {submitError && <Alert severity="error">{submitError}</Alert>}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              required
              fullWidth
              id="join-firstName"
              label="First name"
              name="firstName"
              autoComplete="given-name"
              value={formData.firstName}
              onChange={handleChange}
              error={Boolean(formErrors.firstName)}
              helperText={formErrors.firstName}
              sx={publicTextFieldSx}
            />
            <TextField
              required
              fullWidth
              id="join-lastName"
              label="Last name"
              name="lastName"
              autoComplete="family-name"
              value={formData.lastName}
              onChange={handleChange}
              error={Boolean(formErrors.lastName)}
              helperText={formErrors.lastName}
              sx={publicTextFieldSx}
            />
          </Stack>
          <TextField
            required
            fullWidth
            id="join-email"
            label="Email address"
            name="email"
            type="email"
            autoComplete="email"
            inputProps={{ autoCapitalize: 'none', autoCorrect: 'off' }}
            value={formData.email}
            onChange={handleChange}
            error={Boolean(formErrors.email)}
            helperText={formErrors.email || (allowedDomains.length
              ? `Use your ${describeDomains(allowedDomains)} email. We'll send you a verification link.`
              : "We'll send a verification link to this address.")}
            sx={publicTextFieldSx}
          />
          <TextField
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="join-password"
            autoComplete="new-password"
            value={formData.password}
            onChange={handleChange}
            error={Boolean(formErrors.password)}
            helperText={formErrors.password || 'Use at least 8 characters.'}
            sx={publicTextFieldSx}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            required
            fullWidth
            name="confirmPassword"
            label="Confirm password"
            type={showPassword ? 'text' : 'password'}
            id="join-confirmPassword"
            autoComplete="new-password"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={Boolean(formErrors.confirmPassword)}
            helperText={formErrors.confirmPassword}
            sx={publicTextFieldSx}
          />
          <Box>
            <FormControlLabel
              sx={{ alignItems: 'flex-start', m: 0 }}
              control={(
                <Checkbox
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleChange}
                  sx={checkboxSx}
                />
              )}
              label={(
                <Typography sx={{ ...bodyTextSx, lineHeight: 1.6, pt: 1 }}>
                  I agree to the{' '}
                  <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
                </Typography>
              )}
            />
            {formErrors.agreeTerms && (
              <Typography variant="caption" color="error" display="block">
                {formErrors.agreeTerms}
              </Typography>
            )}
          </Box>
        </Stack>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting || Boolean(signedInUser)}
            sx={{ ...publicPrimaryButtonSx, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}
          >
            {isSubmitting ? 'Creating your account…' : 'Create account'}
          </Button>
          <Button component={Link} to="/login" variant="outlined" sx={{ ...publicSecondaryButtonSx, flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
            I already have an account
          </Button>
        </Box>
      </Box>
    </Stack>
  );

  const renderBlocked = () => (
    <Stack spacing={3}>
      {details?.university && renderDetails()}
      <Alert severity="warning">{blockingError}</Alert>
      <Typography sx={bodyTextSx}>
        Ask your course administrator for a new QR code or sign-up link. If you already have an account, you can sign in.
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        <Button component={Link} to="/login" variant="contained" sx={publicPrimaryButtonSx}>
          Sign in
        </Button>
        <Button component={Link} to="/" variant="outlined" sx={publicSecondaryButtonSx}>
          Visit Virtual Medic
        </Button>
      </Box>
    </Stack>
  );

  let content;
  if (loading) {
    content = (
      <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
        <CircularProgress />
        <Typography sx={bodyTextSx}>Checking your sign-up link…</Typography>
      </Stack>
    );
  } else if (registeredEmail) {
    content = renderSuccess();
  } else if (blockingError) {
    content = renderBlocked();
  } else {
    content = renderForm();
  }

  return (
    <PublicPageFrame>
      <PublicCardHeader
        eyebrow="Student sign-up"
        title={details?.university?.name ? `Join ${details.university.name}` : 'Join your university'}
        description="Create your Virtual Medic account to start practising clinical history-taking with virtual patients."
      />
      {content}
    </PublicPageFrame>
  );
}

export default JoinUniversityPage;
